import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { triggerTabNotification, playChimeSound } from '../../../utils';

/**
 * Hook para gestionar la conexión en tiempo real vía SignalR con la sala del ticket,
 * reconciliar mensajes optimistas y sincronizar eventos de actividad.
 */
export const useTicketSignalR = ({
  activeTicketId,
  token,
  setMessages,
  markCurrentAsRead,
  fetchTicketDetail,
  onTicketUpdated,
  onRefreshHistory,
  ticketDetail,
  propTicket
}) => {
  const hubConnectionRef = useRef(null);
  const fetchTicketDetailRef = useRef(fetchTicketDetail);
  fetchTicketDetailRef.current = fetchTicketDetail;

  const onTicketUpdatedRef = useRef(onTicketUpdated);
  onTicketUpdatedRef.current = onTicketUpdated;

  const onRefreshHistoryRef = useRef(onRefreshHistory);
  onRefreshHistoryRef.current = onRefreshHistory;

  const ticketDetailRef = useRef(ticketDetail);
  ticketDetailRef.current = ticketDetail;

  const markCurrentAsReadRef = useRef(markCurrentAsRead);
  markCurrentAsReadRef.current = markCurrentAsRead;

  useEffect(() => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    let isCancelled = false;
    let connection = null;

    const startSignalR = async () => {
      try {
        const resp = await fetch('/api/SupportEngine/GetBaseUrl', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });
        if (!resp.ok) {
          console.error(`[TicketDetailView] Error al consultar /api/SupportEngine/GetBaseUrl: HTTP ${resp.status}`);
          return;
        }
        const { baseUrl } = await resp.json();
        if (isCancelled) return;

        const hubUrl = `${baseUrl}/hubs/support-chat`;
        connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            skipNegotiation: false,
            transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();

        hubConnectionRef.current = connection;

        connection.onreconnected(() => {
          connection.invoke('JoinTicketGroup', parseInt(activeTicketId)).catch(e => console.error('Error re-uniendo al grupo:', e));
        });

        await connection.start();
        if (isCancelled) {
          connection.stop().catch(() => { });
          return;
        }

        // Unirse a la sala del ticket
        await connection.invoke('JoinTicketGroup', parseInt(activeTicketId));

        // Escuchar mensajes entrantes en tiempo real
        connection.on('ReceiveMessage', async newMsg => {
          if (!newMsg) return;

          // Si el evento recibido es una acción administrativa (ej. asignación) y no un mensaje de chat, ignorar
          if (newMsg.action) return;

          const msgId = newMsg.idMensaje ?? newMsg.messageId;
          const msgText = newMsg.vTextoMensaje ?? newMsg.messageText ?? '';
          const mediaId = newMsg.vMediaId ?? newMsg.mediaId;
          const isAgent = newMsg.bEsAgente ?? newMsg.isAgent;

          const formattedMsg = {
            idMensaje: msgId,
            idTicket: newMsg.idTicket ?? newMsg.ticketId,
            bEsAgente: isAgent ? 1 : 0,
            idCatTipoMensaje: newMsg.idCatTipoMensaje ?? newMsg.messageTypeId ?? 1,
            vTextoMensaje: msgText,
            vMediaId: mediaId,
            vMediaMimeType: newMsg.vMediaMimeType ?? newMsg.mediaMimeType,
            vMediaUrl: newMsg.vMediaUrl ?? newMsg.mediaUrl,
            vFromNumber: newMsg.vFromNumber ?? newMsg.fromNumber,
            vFromUserId: newMsg.vFromUserId ?? newMsg.fromUserId,
            vCreatedUser: newMsg.vCreatedUser ?? newMsg.createdUser,
            dCreatedDate: newMsg.dCreatedDate ?? newMsg.createdDate ?? new Date().toISOString()
          };

          setMessages(prev => {
            // 1. Evitar duplicados solo si tiene un ID válido (evita que undefined === undefined descarte mensajes)
            const exists = prev.some(m =>
              (msgId && (m.idMensaje === msgId || m.messageId === msgId)) ||
              (mediaId && (m.vMediaId === mediaId || m.mediaId === mediaId))
            );
            if (exists) {
              return prev;
            }

            // 2. Si es un mensaje de agente y hay un mensaje temporal optimista pendiente con el mismo contenido, reemplazarlo
            const tempIndex = prev.findIndex(m =>
              (m.isTemp || m.idMensaje > 1000000000000) &&
              (
                (m.vTextoMensaje && msgText && m.vTextoMensaje.trim() === msgText.trim()) ||
                (m.vMediaUrl && formattedMsg.vMediaUrl && m.vMediaUrl === formattedMsg.vMediaUrl) ||
                (m.vMediaId && mediaId && m.vMediaId === mediaId)
              )
            );

            if (tempIndex !== -1) {
              const updated = [...prev];
              updated[tempIndex] = formattedMsg;
              return updated;
            }
            return [...prev, formattedMsg];
          });

          const isFromClient = isAgent === 0 || isAgent === false || formattedMsg.bEsAgente === 0;

          // Si la pestaña está en segundo plano o el usuario está en otra ventana/pestaña, animar pestaña y alertar
          const isTabInactive = typeof document === 'undefined' || document.hidden || !document.hasFocus();

          // Solo mantenerlo como leído automáticamente si el asesor TIENE la pestaña activa y en foco
          if (!isTabInactive && markCurrentAsReadRef.current) {
            markCurrentAsReadRef.current(activeTicketId);
          }

          if (isTabInactive && isFromClient) {
            const customerName = ticketDetailRef.current?.vCustomerName || propTicket?.vCustomerName || 'Cliente';
            triggerTabNotification({
              alertText: '¡Nuevo mensaje!',
              detailText: `Ticket #${activeTicketId} · ${customerName}`
            });

          playChimeSound();

            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
              try {
                const notif = new Notification(`Ticket #${activeTicketId} · ${customerName}`, {
                  body: formattedMsg.vTextoMensaje || 'Nuevo mensaje recibido',
                  icon: '/favicon.ico',
                  tag: `ticket-${activeTicketId}`,
                  renotify: true
                });
                notif.onclick = () => {
                  window.focus();
                  notif.close();
                };
              } catch {
                // Fallback silencioso
              }
            }
          }

          // Notificar reactivamente a la lista de tickets para actualizar última actividad
          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(activeTicketId),
              bEsAgente: isFromClient ? 0 : 1,
              isAgent: !isFromClient,
              vTextoMensaje: newMsg.vTextoMensaje || newMsg.textoMensaje || newMsg.messageText || '',
              dUltimaActividad: newMsg.dCreatedDate || newMsg.createdDate || new Date().toISOString(),
              isCurrentlyOpen: true
            }
          }));

          // Si el ticket estaba cerrado y un cliente responde (reapertura), refrescamos estado e historial
          const currentStatus = Number(ticketDetailRef.current?.iEstadoTicket ?? propTicket?.iEstadoTicket);
          if (isFromClient && currentStatus === 2) {
            if (fetchTicketDetailRef.current) {
              await fetchTicketDetailRef.current();
            }
            if (onTicketUpdatedRef.current) {
              onTicketUpdatedRef.current();
            }
            if (onRefreshHistoryRef.current) {
              onRefreshHistoryRef.current();
            }
          }
        });

        // Escuchar solicitud de refresco de detalles del ticket (ej. mensaje desde otro número del cliente)
        connection.on('ReceiveTicketDetailRefresh', async refreshData => {
          if (fetchTicketDetailRef.current) {
            await fetchTicketDetailRef.current(true);
          }
          if (onTicketUpdatedRef.current) {
            onTicketUpdatedRef.current();
          }
          if (onRefreshHistoryRef.current) {
            onRefreshHistoryRef.current();
          }
          if (refreshData?.idClienteAkasia) {
            window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
              detail: {
                idTicket: parseInt(activeTicketId),
                idClienteAkasia: refreshData.idClienteAkasia
              }
            }));
          }
        });
      } catch (err) {
        console.warn('[TicketDetailView] Error en conexión SignalR:', err);
      }
    };

    startSignalR();

    return () => {
      isCancelled = true;
      if (connection) {
        connection.invoke('LeaveTicketGroup', parseInt(activeTicketId)).catch(() => { });
        connection.stop().catch(() => { });
      }
    };
  }, [activeTicketId, token, setMessages]);
};
